import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  DollarSign,
  Plus,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Filter,
  Calendar,
  Users,
  TrendingUp,
  FileText,
  AlertCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DISTRIBUTION_TYPES = [
  { value: 'interest', label: 'Interest Payment' },
  { value: 'capital_return', label: 'Capital Return' },
  { value: 'special_dividend', label: 'Special Dividend' },
  { value: 'final_settlement', label: 'Final Settlement' }
];

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  processing: { label: 'Processing', color: 'bg-purple-100 text-purple-700', icon: RefreshCw },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-700', icon: XCircle }
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2
  }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export default function AdminDistributionsPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  // State
  const [loading, setLoading] = useState(true);
  const [distributions, setDistributions] = useState([]);
  const [batches, setBatches] = useState([]);
  const [summary, setSummary] = useState({ total_gross: 0, total_tax: 0, total_net: 0, count: 0 });
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 0 });
  const [activeTab, setActiveTab] = useState('distributions');
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [showMarkPaidDialog, setShowMarkPaidDialog] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    distribution_date: new Date().toISOString().split('T')[0],
    distribution_type: 'interest',
    tax_rate: 15,
    description: ''
  });
  const [paymentRef, setPaymentRef] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch distributions
  const fetchDistributions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('page_size', pagination.pageSize);
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('distribution_type', typeFilter);
      if (dateFilter) {
        params.append('start_date', `${dateFilter}-01`);
        params.append('end_date', `${dateFilter}-31`);
      }

      const response = await axios.get(`${API_URL}/api/admin/distributions?${params}`, { headers });
      setDistributions(response.data.items || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        totalPages: response.data.total_pages
      }));
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Error fetching distributions:', error);
      toast.error('Failed to load distributions');
    } finally {
      setLoading(false);
    }
  }, [token, pagination.page, pagination.pageSize, statusFilter, typeFilter, dateFilter]);

  // Fetch batches
  const fetchBatches = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/distributions/batches`, { headers });
      setBatches(response.data.batches || []);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchDistributions();
    fetchBatches();
  }, [fetchDistributions, fetchBatches]);

  // Create batch distributions
  const handleCreateBatch = async () => {
    if (!formData.distribution_date) {
      toast.error('Please select a distribution date');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/distributions/batch`, formData, { headers });
      toast.success(`Created ${response.data.created} distributions`);
      setShowBatchDialog(false);
      fetchDistributions();
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create batch');
    } finally {
      setSubmitting(false);
    }
  };

  // Approve batch
  const handleApproveBatch = async (batchId) => {
    try {
      await axios.put(`${API_URL}/api/admin/distributions/batch/${batchId}/approve`, {}, { headers });
      toast.success('Batch approved');
      fetchDistributions();
      fetchBatches();
    } catch (error) {
      toast.error('Failed to approve batch');
    }
  };

  // Mark batch as paid
  const handleMarkPaid = async () => {
    if (!paymentRef) {
      toast.error('Please enter a payment reference');
      return;
    }

    try {
      await axios.put(
        `${API_URL}/api/admin/distributions/batch/${selectedBatch.id}/mark-paid?payment_reference=${encodeURIComponent(paymentRef)}`,
        {},
        { headers }
      );
      toast.success('Batch marked as paid');
      setShowMarkPaidDialog(false);
      setPaymentRef('');
      fetchDistributions();
      fetchBatches();
    } catch (error) {
      toast.error('Failed to mark as paid');
    }
  };

  if (loading && distributions.length === 0) {
    return (
      <div className="space-y-6" data-testid="distributions-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-distributions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="h-6 w-6" />
            Distribution Management
          </h1>
          <p className="text-gray-500 mt-1">
            Process investor interest payments and capital returns
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              fetchDistributions();
              fetchBatches();
              toast.success('Refreshed');
            }}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => {
              setFormData({
                distribution_date: new Date().toISOString().split('T')[0],
                distribution_type: 'interest',
                tax_rate: 15,
                description: ''
              });
              setShowBatchDialog(true);
            }}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            data-testid="create-batch-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Batch Distribution
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Gross</p>
                <p className="text-xl font-bold text-gray-900">{formatCurrency(summary.total_gross)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-gray-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total WHT</p>
                <p className="text-xl font-bold text-red-600">{formatCurrency(summary.total_tax)}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Net</p>
                <p className="text-xl font-bold text-green-600">{formatCurrency(summary.total_net)}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Count</p>
                <p className="text-xl font-bold text-gray-900">{summary.count}</p>
              </div>
              <FileText className="h-8 w-8 text-gray-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="distributions">All Distributions</TabsTrigger>
          <TabsTrigger value="batches">Batches</TabsTrigger>
        </TabsList>

        <TabsContent value="distributions" className="mt-4">
          {/* Filters */}
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="w-40">
                  <Label className="text-xs text-gray-500">Status</Label>
                  <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                        <SelectItem key={value} value={value}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-40">
                  <Label className="text-xs text-gray-500">Type</Label>
                  <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {DISTRIBUTION_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-40">
                  <Label className="text-xs text-gray-500">Month</Label>
                  <Input
                    type="month"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                  />
                </div>
                <Button variant="outline" onClick={() => {
                  setStatusFilter('');
                  setTypeFilter('');
                  setDateFilter('');
                }}>
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Distributions Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Date</TableHead>
                    <TableHead>Investor</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">WHT</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Reference</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {distributions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-gray-500">
                        No distributions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    distributions.map(dist => {
                      const statusConfig = STATUS_CONFIG[dist.status] || STATUS_CONFIG.pending;
                      const StatusIcon = statusConfig.icon;
                      return (
                        <TableRow key={dist.id}>
                          <TableCell>{formatDate(dist.distribution_date)}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{dist.investor_name}</p>
                              <p className="text-xs text-gray-500">{dist.investor_email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="capitalize">{dist.distribution_type?.replace('_', ' ')}</TableCell>
                          <TableCell className="text-right">{formatCurrency(dist.gross_amount)}</TableCell>
                          <TableCell className="text-right text-red-600">{formatCurrency(dist.tax_withheld)}</TableCell>
                          <TableCell className="text-right font-medium text-green-600">{formatCurrency(dist.net_amount)}</TableCell>
                          <TableCell>
                            <Badge className={statusConfig.color}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">{dist.reference || '-'}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t">
                  <p className="text-sm text-gray-500">
                    Showing {((pagination.page - 1) * pagination.pageSize) + 1} to{' '}
                    {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                      disabled={pagination.page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="px-3 py-1 text-sm">{pagination.page} / {pagination.totalPages}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                      disabled={pagination.page === pagination.totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="batches" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Distribution Batches</CardTitle>
              <CardDescription>Manage batch distribution runs</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Distributions</TableHead>
                    <TableHead className="text-right">Total Gross</TableHead>
                    <TableHead className="text-right">Total Net</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-gray-500">
                        No batches found. Create one to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    batches.map(batch => {
                      const statusConfig = STATUS_CONFIG[batch.status] || STATUS_CONFIG.pending;
                      return (
                        <TableRow key={batch.id}>
                          <TableCell>{formatDate(batch.distribution_date)}</TableCell>
                          <TableCell className="capitalize">{batch.distribution_type}</TableCell>
                          <TableCell className="text-right">{batch.total_distributions}</TableCell>
                          <TableCell className="text-right">{formatCurrency(batch.total_gross)}</TableCell>
                          <TableCell className="text-right font-medium text-green-600">
                            {formatCurrency(batch.total_net)}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {batch.status === 'created' && (
                                <Button size="sm" variant="outline" onClick={() => handleApproveBatch(batch.id)}>
                                  Approve
                                </Button>
                              )}
                              {(batch.status === 'approved' || batch.status === 'created') && (
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setSelectedBatch(batch);
                                    setShowMarkPaidDialog(true);
                                  }}
                                >
                                  Mark Paid
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Batch Dialog */}
      <Dialog open={showBatchDialog} onOpenChange={setShowBatchDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Create Batch Distribution
            </DialogTitle>
            <DialogDescription>
              Automatically calculate and create distributions for all active investors
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Distribution Date *</Label>
              <Input
                type="date"
                value={formData.distribution_date}
                onChange={(e) => setFormData(p => ({ ...p, distribution_date: e.target.value }))}
              />
            </div>
            
            <div>
              <Label>Distribution Type</Label>
              <Select
                value={formData.distribution_type}
                onValueChange={(v) => setFormData(p => ({ ...p, distribution_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DISTRIBUTION_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>WHT Rate (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={formData.tax_rate}
                onChange={(e) => setFormData(p => ({ ...p, tax_rate: parseFloat(e.target.value) }))}
              />
              <p className="text-xs text-gray-500 mt-1">Standard SA withholding tax is 15%</p>
            </div>
            
            <div>
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                placeholder="e.g., January 2026 interest distribution"
              />
            </div>
            
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                This will calculate interest due on all active loan participations and create pending distributions.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBatchDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateBatch}
              disabled={submitting}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            >
              {submitting ? 'Creating...' : 'Create Distributions'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Paid Dialog */}
      <Dialog open={showMarkPaidDialog} onOpenChange={setShowMarkPaidDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Batch as Paid</DialogTitle>
            <DialogDescription>
              Enter the payment reference for this batch
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm"><strong>Date:</strong> {formatDate(selectedBatch?.distribution_date)}</p>
              <p className="text-sm"><strong>Distributions:</strong> {selectedBatch?.total_distributions}</p>
              <p className="text-sm"><strong>Total Net:</strong> {formatCurrency(selectedBatch?.total_net)}</p>
            </div>
            
            <Label>Payment Reference *</Label>
            <Input
              value={paymentRef}
              onChange={(e) => setPaymentRef(e.target.value)}
              placeholder="e.g., INVEST-PAY-2026-01"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMarkPaidDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleMarkPaid}>
              Mark as Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
